/**
 * Modal de ajuda para cada tipo de node
 * Fornece documentação, regras, exemplos e dicas de uso
 */

import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { AlertCircle, CheckCircle, Lightbulb, Code } from 'lucide-react';

interface NodeHelpContent {
  title: string;
  description: string;
  rules: string[];
  examples?: string[];
  operators?: Record<string, string>;
  tips?: string[];
}

// Conteúdo de ajuda para cada tipo de node
const nodeHelpData: Record<string, NodeHelpContent> = {
  message_text: {
    title: 'Enviar Mensagem de Texto',
    description: 'Envia uma mensagem de texto personalizada para o contato.',
    rules: [
      'Suporta variáveis usando a sintaxe {{nome_variavel}}',
      'Máximo de 4096 caracteres',
      'Variáveis são substituídas automaticamente pelos valores coletados',
    ],
    examples: [
      'Olá {{nome}}, tudo bem?',
      'Seu protocolo é: {{protocolo}}',
      'Obrigado por escolher {{opcao_escolhida}}!',
    ],
    tips: [
      'Use variáveis coletadas anteriormente via "Coletar Dado" ou "Pergunta"',
      'Personalize as mensagens para criar experiências únicas',
      'Teste as variáveis antes de publicar o flow',
    ],
  },

  message_media: {
    title: 'Enviar Mídia',
    description: 'Envia imagem, vídeo, áudio ou documento com legenda opcional.',
    rules: [
      'URL da mídia deve ser pública e acessível',
      'Legenda é opcional e suporta variáveis',
      'Formatos suportados: imagem (JPG, PNG), vídeo (MP4), áudio (MP3, OGG), documento (PDF, DOC)',
    ],
    examples: [
      'https://exemplo.com/catalogo.pdf',
      'https://exemplo.com/video-demo.mp4',
    ],
    tips: [
      'Teste a URL antes de usar no flow',
      'Use legendas para contexto adicional',
      'Evite arquivos muito grandes (max 16MB)',
    ],
  },

  question_buttons: {
    title: 'Pergunta com Botões',
    description: 'Apresenta opções clicáveis para o usuário escolher.',
    rules: [
      'Máximo de 3 botões por pergunta',
      'Cada botão gera uma saída diferente no fluxo',
      'Conecte CADA saída ao próximo passo correspondente',
    ],
    examples: [
      'Qual departamento deseja falar? [Comercial] [Suporte] [Financeiro]',
      'Possui cadastro? [Sim] [Não]',
    ],
    tips: [
      'Conecte a saída de cada botão ao node correspondente',
      'Use labels claros e diretos',
      'Teste todos os caminhos possíveis',
    ],
  },

  question_list: {
    title: 'Pergunta com Lista',
    description: 'Apresenta lista de opções para o usuário escolher. Suporta opções fixas (definidas manualmente) ou dinâmicas (vindas de APIs/webhooks).',
    rules: [
      'Máximo de 10 opções por lista',
      'Cada opção pode ter título, valor e descrição',
      '📌 Lista Estática: opções definidas manualmente na configuração',
      '🔄 Lista Dinâmica: opções geradas automaticamente a partir de uma variável (resposta de webhook/API)',
      'Para listas dinâmicas, configure os "caminhos" dos campos do JSON',
    ],
    examples: [
      '📌 ESTÁTICO - Opções fixas:\n   "Escolha um plano:" → Básico ($10) | Pro ($50) | Enterprise',
      '🔄 DINÂMICO - Opções de API:\n   1. Webhook consulta API com CPF do cliente\n   2. API retorna: [{marca: "Fiat", placa: "ABC1234"}, ...]\n   3. Sistema gera lista automaticamente com os veículos\n   4. Cliente escolhe qual veículo quer consultar',
      '⚙️ CONFIGURAÇÃO DINÂMICA:\n   • Fonte: "Variável do fluxo"\n   • Variável: resposta_api (nome da variável do webhook)\n   • Campo do Label: veiculo_marca (o que APARECE na lista)\n   • Campo do Valor: veiculo_placa (o que é SALVO)\n   • Campo da Descrição: status (informação extra, opcional)',
    ],
    tips: [
      '💡 Use listas dinâmicas quando as opções vêm de sistemas externos (ERP, CRM, APIs)',
      '💡 O "Campo do Label" define o que o usuário VÊ na lista',
      '💡 O "Campo do Valor" define o que será SALVO na variável de resposta',
      '💡 Se a API retornar array vazio, será exibida mensagem informativa',
      '💡 Teste primeiro o webhook para garantir que retorna dados corretos',
      '💡 Use o botão "Testar" no webhook para ver exatamente o formato do JSON',
      '💡 O caminho do campo é o nome exato do campo no JSON (ex: "nome", "placa", "id")',
    ],
  },

  input_data: {
    title: 'Coletar Dado',
    description: 'Solicita uma informação específica do usuário e armazena em variável.',
    rules: [
      'Nome da variável deve ser único no flow',
      'Use snake_case: nome_cliente, idade, email',
      'Tipos suportados: texto, número, email, telefone, data',
      'Validação automática baseada no tipo escolhido',
    ],
    examples: [
      'Variável: nome_cliente, Tipo: texto, Pergunta: "Qual seu nome?"',
      'Variável: idade, Tipo: número, Pergunta: "Quantos anos você tem?"',
      'Variável: email, Tipo: email, Pergunta: "Digite seu e-mail:"',
    ],
    tips: [
      'Valide dados importantes antes de prosseguir',
      'Use mensagens claras de erro para o usuário',
      'O nome da variável é case-sensitive',
    ],
  },

  condition: {
    title: 'Condição Lógica',
    description: 'Cria ramificação baseada em comparação de variáveis.',
    rules: [
      'A variável DEVE ter sido coletada anteriormente',
      'Saída VERDE (direita) = condição VERDADEIRA',
      'Saída VERMELHA (esquerda) = condição FALSA',
      'Sempre conecte AMBAS as saídas',
    ],
    operators: {
      '==': 'Igual a (texto ou número)',
      '!=': 'Diferente de',
      '>': 'Maior que (apenas números)',
      '<': 'Menor que (apenas números)',
      '>=': 'Maior ou igual (apenas números)',
      '<=': 'Menor ou igual (apenas números)',
      'contains': 'Contém texto (case-insensitive)',
      'starts_with': 'Começa com',
      'ends_with': 'Termina com',
    },
    examples: [
      'tipo_cliente == "PF" → Verdadeiro se for Pessoa Física',
      'idade > 18 → Verdadeiro se maior que 18 anos',
      'email contains "@gmail" → Verdadeiro se contém @gmail',
      'nome starts_with "João" → Verdadeiro se nome começa com João',
    ],
    tips: [
      'Use "Coletar Dado" ANTES para capturar a variável',
      'O nome da variável é case-sensitive',
      'Operadores de texto são case-insensitive',
      'Conecte AMBAS as saídas (verdadeiro e falso)',
      'Teste com diferentes valores para garantir lógica correta',
    ],
  },

  delay: {
    title: 'Aguardar',
    description: 'Pausa a execução do flow por um período determinado.',
    rules: [
      'Tempo mínimo: 1 segundo',
      'Unidades: segundos, minutos, horas, dias',
      'Ideal para simular tempo de processamento',
    ],
    examples: [
      '5 segundos - pausa rápida entre mensagens',
      '2 minutos - aguardar resposta',
      '1 dia - envio programado',
    ],
    tips: [
      'Use delays curtos entre mensagens para parecer natural',
      'Evite delays muito longos sem feedback ao usuário',
      'Considere usar gatilhos agendados para delays longos',
    ],
  },

  add_tag: {
    title: 'Adicionar Tag',
    description: 'Adiciona uma tag ao contato para segmentação.',
    rules: [
      'Nome da tag é criado automaticamente se não existir',
      'Use snake_case ou kebab-case',
      'Tags ajudam na organização e filtros',
    ],
    examples: [
      'cliente_vip',
      'interessado-plano-pro',
      'aguardando-pagamento',
    ],
    tips: [
      'Use tags para segmentar contatos',
      'Crie uma convenção de nomes consistente',
      'Tags podem acionar outros flows',
    ],
  },

  update_contact: {
    title: 'Atualizar Contato',
    description: 'Atualiza campos do cadastro do contato.',
    rules: [
      'Campos: nome, email, metadados (JSON)',
      'Suporta variáveis: {{nome_cliente}}',
      'Metadados úteis para informações customizadas',
    ],
    examples: [
      'Campo: nome, Valor: {{nome_cliente}}',
      'Campo: email, Valor: {{email_informado}}',
    ],
    tips: [
      'Mantenha dados atualizados para personalização',
      'Use metadados para informações não-padrão',
    ],
  },

  transfer_human: {
    title: 'Transferir para Agente',
    description: 'Transfere a conversa para um agente humano.',
    rules: [
      'Selecione o agente de destino',
      'A conversa sai do flow automaticamente',
      'Útil quando automação não é suficiente',
    ],
    tips: [
      'Configure condições claras para transferência',
      'Informe ao usuário sobre a transferência',
      'Garanta que agentes estejam disponíveis',
    ],
  },

  transfer_department: {
    title: 'Transferir para Departamento',
    description: 'Transfere a conversa para fila de departamento.',
    rules: [
      'Departamento deve estar ativo',
      'Conversa entra na fila do departamento',
      'Primeiro agente disponível atende',
    ],
    tips: [
      'Use para distribuição equilibrada de atendimentos',
      'Configure departamentos antes de usar',
    ],
  },

  webhook: {
    title: 'Webhook / API Externa',
    description: 'Conecta seu fluxo a sistemas externos (ERPs, CRMs, APIs) para buscar ou enviar dados. Os dados retornados podem ser salvos em variáveis para uso em mensagens, condições e listas dinâmicas.',
    rules: [
      'URL deve ser pública e retornar JSON válido',
      'Métodos: GET (buscar dados), POST (enviar dados), PUT, PATCH, DELETE',
      'Timeout máximo: 30 segundos',
      'Use credenciais para APIs que exigem autenticação (API Key, Bearer Token, Basic Auth)',
      '📦 A resposta pode ser salva INTEIRA ou você pode MAPEAR campos específicos',
      '🔗 Use variáveis do fluxo no body: {"cpf": "{{cpf_cliente}}"}',
    ],
    examples: [
      '🔍 CONSULTAR API:\n' +
      '   • URL: https://api.exemplo.com/consultar\n' +
      '   • Método: POST\n' +
      '   • Body: {"cpf": "{{cpf_cliente}}"}\n' +
      '   → API retorna dados do cliente',

      '💾 SALVAR RESPOSTA:\n' +
      '   1. "Resposta completa": salva TODO o JSON em uma variável\n' +
      '      Útil para usar em Listas Dinâmicas (opções de API)\n' +
      '   2. "Mapeamento de Campos": extrai campos específicos\n' +
      '      Ex: nome, email, saldo ficam em variáveis separadas',

      '📋 MAPEAMENTO DE CAMPOS:\n' +
      '   Coluna 1 - Caminho no JSON: onde está o dado na resposta da API\n' +
      '   Coluna 2 - Variável: qual variável receberá o valor (seletor integrado)\n\n' +
      '   Exemplo - API retorna:\n' +
      '   {"usuario": {"nome": "João", "idade": 30}, "status": "ativo"}\n\n' +
      '   Mapeamentos:\n' +
      '   • usuario.nome → variável: nome_cliente\n' +
      '   • usuario.idade → variável: idade_cliente\n' +
      '   • status → variável: status_conta',

      '📦 ARRAYS (listas de itens):\n' +
      '   Se a API retornar um array de objetos:\n' +
      '   [{"placa": "ABC-1234", "marca": "Fiat"}, {"placa": "XYZ-5678", "marca": "VW"}]\n\n' +
      '   Para pegar item específico: [0].placa → "ABC-1234"\n' +
      '   Para usar TODO o array: salve em "Resposta completa"\n' +
      '   e use em "Pergunta Lista" com fonte "Variável"',
    ],
    tips: [
      '💡 Use o botão "Testar" para ver exatamente o que a API retorna',
      '💡 Clique nos campos sugeridos após o teste para mapear automaticamente',
      '💡 Crie novas variáveis diretamente no seletor, se necessário',
      '💡 Para arrays, use índice: [0].campo (primeiro item), [1].campo (segundo item)',
      '💡 Configure credenciais em "Configurações > Credenciais" antes de usar APIs protegidas',
      '💡 Para listas dinâmicas: salve a resposta inteira → use no node "Pergunta Lista" com fonte "Variável"',
      '💡 O seletor de variáveis mostra todas as variáveis disponíveis no tenant',
    ],
  },

  set_variable: {
    title: 'Definir Variável',
    description: 'Atribui um valor a uma variável global do tenant.',
    rules: [
      'Variável deve existir ou ser criada previamente',
      'Tipos de valor: Estático, Variável (cópia), ou Expressão',
      'Variáveis definidas ficam disponíveis para uso em qualquer ponto do flow',
      'Expressões suportam múltiplas variáveis: {{var1}} {{var2}}',
    ],
    examples: [
      'Estático: nome_completo = "João Silva"',
      'Variável: nome_formatado = {{nome_cliente}}',
      'Expressão: mensagem_completa = "Olá {{nome}}, seu CPF é {{cpf}}"',
      'Cálculo: total = {{quantidade}} x {{preco}}',
    ],
    tips: [
      'Use variáveis globais para reutilização entre múltiplos flows',
      'Tipo "Expressão" permite combinar texto e variáveis',
      'Útil para preparar dados antes de enviar mensagens ou webhooks',
      'Pode ser usado como "cache" de valores calculados',
    ],
  },

  goto: {
    title: 'Pular para Node',
    description: 'Pula diretamente para outro node do fluxo, ignorando conexões.',
    rules: [
      'Selecione o node destino pelo ID',
      'NÃO requer conexão de saída no canvas',
      'Ignora a estrutura de conexões e vai direto ao destino',
      'Não pode pular para Start, End ou outro Goto',
    ],
    examples: [
      'Pular para node "question_buttons-123" após condição específica',
      'Criar loops voltando para perguntas anteriores',
      'Implementar fluxos não-lineares complexos',
    ],
    tips: [
      'Use com cuidado para evitar loops infinitos',
      'Ideal para criar "voltar ao menu principal"',
      'O ID do node destino aparece nos modais de configuração',
      'Útil quando a lógica de fluxo não se encaixa em conexões lineares',
    ],
  },

  format_array: {
    title: 'Formatar Array',
    description: 'Transforma um array de objetos em texto formatado usando um template. Ideal para exibir listas de dados (boletos, pedidos, produtos) em mensagens de texto personalizadas.',
    rules: [
      'A variável de entrada deve conter um array válido (ex: resposta de webhook)',
      'Use {{campo}} para inserir valores de cada item do array',
      'Use {{$index}} para exibir o número sequencial do item (1, 2, 3...)',
      'O resultado é salvo em uma variável de saída para uso em Message Text',
      'Opção de filtro permite filtrar itens antes de formatar',
      'Header e footer são opcionais para adicionar texto antes/depois da lista',
    ],
    examples: [
      '📋 CASO 1 - Listar Boletos:\n' +
      '   Variável de entrada: boletos_abertos\n' +
      '   Template:\n' +
      '   📄 *Boleto {{$index}}*\n' +
      '   Código: {{codigo_barras}}\n' +
      '   Link: {{link_boleto}}\n' +
      '   Valor: R$ {{valor}}',
      
      '📦 CASO 2 - Listar Produtos:\n' +
      '   Variável de entrada: produtos_carrinho\n' +
      '   Template:\n' +
      '   🛒 {{nome_produto}}\n' +
      '   Qtd: {{quantidade}} x R$ {{preco_unitario}}',
      
      '🚗 CASO 3 - Listar Veículos com Filtro:\n' +
      '   Variável de entrada: todos_veiculos\n' +
      '   Filtro: veiculo_modelo = {{modelo_escolhido}}\n' +
      '   Template:\n' +
      '   🚗 Placa: {{placa}}\n' +
      '   Status: {{status}}',
      
      '📊 RESULTADO FINAL no Message Text:\n' +
      '   "Seus boletos:\n\n{{lista_formatada}}"',
    ],
    tips: [
      '💡 Use após um Webhook que retorna array de dados',
      '💡 Combine com Filter Array para filtrar antes de formatar',
      '💡 O separador padrão é \\n\\n (duas linhas em branco)',
      '💡 Use o filtro integrado para mostrar apenas itens específicos',
      '💡 Header/Footer suportam variáveis: "Boletos do veículo {{veiculo_nome}}"',
      '💡 Se o array estiver vazio, exibe a mensagem de "array vazio" configurada',
      '💡 A variável de saída pode ser usada em qualquer node posterior',
    ],
  },

  filter_array: {
    title: 'Filtrar Array',
    description: 'Filtra itens de um array baseado em uma condição. Cria um novo array contendo apenas os itens que atendem ao critério especificado.',
    rules: [
      'A variável de entrada deve conter um array válido',
      'O campo de filtro é o caminho para a propriedade a ser comparada',
      'O resultado é um NOVO array (não modifica o original)',
      'Se nenhum item atender à condição, retorna array vazio',
    ],
    examples: [
      'Filtrar boletos: status == "A" → boletos em aberto',
      'Filtrar pedidos: valor > 100 → pedidos acima de R$100',
      'Filtrar produtos: estoque > 0 → produtos disponíveis',
    ],
    tips: [
      '💡 Use antes de Format Array para preparar os dados',
      '💡 O array filtrado mantém a estrutura completa de cada item',
      '💡 Comparações de texto são case-insensitive',
      '💡 Pode encadear múltiplos Filter Array em sequência',
    ],
  },

  // Node de Carrossel Multi-Provider (RELEASE: 1.1.0 - Suporte Meta WABA)
  message_carousel: {
    title: 'Enviar Carrossel',
    description: 'Envia múltiplos cartões com imagem, texto e botões interativos. Ideal para catálogos de produtos, menus visuais e apresentações.',
    rules: [
      'Z-API: Mínimo 1 cartão, máximo 10 cartões, até 3 botões por cartão',
      'Meta WABA: Mínimo 2 cartões, máximo 10, botões devem ser consistentes entre cartões',
      'Meta WABA: Apenas botões URL ou Quick Reply (CALL não suportado)',
      'Cada cartão requer imagem (URL pública) e texto',
      'Mensagem principal é opcional (exibida acima dos cartões)',
      'Suporta variáveis {{nome}} nos textos dos cartões',
    ],
    examples: [
      'Catálogo: 4 cartões com produtos, cada um com botão "Ver Detalhes" (URL)',
      'Menu Visual: 3 opções de serviço com botões "Escolher" (Quick Reply)',
      'Promoções: 5 ofertas com botão "Comprar Agora" em cada (URL)',
    ],
    tips: [
      '💡 Use imagens com proporção 1.91:1 (800x418px) para melhor exibição',
      '💡 Meta WABA: Todos os cartões devem ter mesmo tipo/quantidade de botões',
      '💡 Meta WABA: Texto do cartão limitado a 160 caracteres',
      '💡 Botões de "Quick Reply" retornam texto capturável pelo fluxo',
      '💡 Limite de 20 caracteres por texto de botão',
    ],
  },

  // Nodes de Human Takeover (Pausa de Bot)
  pause_bot: {
    title: 'Pausar Bot (Human Takeover)',
    description: 'Pausa a automação do bot para permitir que um atendente humano converse com o contato sem interferência do bot.',
    rules: [
      'Pausa Indefinida: bot fica pausado até que a conversa seja fechada ou agente reative',
      'Pausa Temporizada: bot é reativado automaticamente após X minutos',
      'O bot é automaticamente reativado quando a conversa é fechada (status = closed)',
      'Mensagens do contato NÃO disparam novos fluxos enquanto o bot estiver pausado',
    ],
    examples: [
      'Menu: "Continuar com atendente" → Pausar Bot → "Ok! O atendente irá responder."',
      'Transferência: Transferir para Depto → Pausar Bot indefinido',
      'Timeout: Pausar Bot por 30 min → Se não responder, reativa e continua fluxo',
    ],
    tips: [
      '💡 Use após menus onde o usuário escolhe falar com humano',
      '💡 Pausa temporizada é útil para dar tempo ao atendente responder',
      '💡 Combine com "Transferir para Departamento" para distribuição automática',
      '💡 Agentes podem pausar/reativar manualmente pelo chat',
      '💡 O fechamento da conversa sempre reativa o bot',
    ],
  },

  resume_bot: {
    title: 'Reativar Bot',
    description: 'Reativa a automação do bot que foi pausada anteriormente, permitindo que o fluxo continue normalmente.',
    rules: [
      'Só faz sentido usar após um "Pausar Bot"',
      'O bot também é reativado automaticamente ao fechar a conversa',
      'Após reativação, próxima mensagem do contato pode disparar novos fluxos',
    ],
    examples: [
      'Menu: "Voltar ao atendimento automático" → Reativar Bot → Menu principal',
      'Finalização manual: Agente clica em "Reativar Bot" no chat',
    ],
    tips: [
      '💡 Use em menus onde usuário pode escolher voltar ao bot',
      '💡 Não é obrigatório - fechar a conversa já reativa automaticamente',
      '💡 Útil para fluxos onde o atendimento humano é temporário',
    ],
  },

  // Node de Atribuição de Fase do Kanban
  assign_phase: {
    title: 'Adicionar Fase',
    description: 'Define a fase do Kanban para a conversa, permitindo visualização e organização no quadro de atendimento.',
    rules: [
      'Primeiro selecione a Categoria do Kanban',
      'Depois escolha a Fase desejada dentro da categoria',
      'A conversa aparecerá no Kanban na fase selecionada',
      'Apenas categorias e fases ativas são listadas',
    ],
    examples: [
      'Atendimento → Novo',
      'Vendas → Em Negociação',
      'Suporte → Aguardando Cliente',
      'Financeiro → Pendente Pagamento',
    ],
    tips: [
      '💡 Use para organizar o fluxo de atendimento visualmente',
      '💡 Facilita a visualização do pipeline de vendas',
      '💡 A fase pode ser alterada em qualquer ponto do fluxo',
      '💡 Configure categorias e fases em Configurações → Kanban',
      '💡 Use as variáveis {{phase_name}} e {{category_name}} em mensagens posteriores',
    ],
  },

  // Nodes CRM - Criar Negócio
  crm_create_deal: {
    title: 'Criar Negócio CRM',
    description: 'Cria automaticamente um novo negócio no pipeline do CRM a partir do fluxo de automação.',
    rules: [
      'Selecione Workspace → Pipeline → Fase (hierarquia em cascata)',
      'Origem do lead é obrigatória (chatbot, site, indicação, etc.)',
      'Vendedor é opcional - pode ser atribuído posteriormente',
      'Título pode ser automático, fixo ou de variável',
      'A variável de saída permite usar o ID do deal em nodes posteriores',
    ],
    examples: [
      'Destino: Vendas → Pipeline Principal → Novo Lead',
      'Título automático: "João Silva - 15/01/2025"',
      'Título de variável: {{titulo_customizado}}',
      'Salvar ID em: deal_id → Usar em "Alterar Fase"',
    ],
    tips: [
      '💡 Use após coletar dados do contato para criar o deal',
      '💡 Salve o ID em uma variável para usar em nodes posteriores',
      '💡 Combine com "Alterar Fase" para mover o deal conforme o fluxo',
      '💡 O título automático usa o nome do contato + data atual',
      '💡 Variáveis como {{contact_name}} funcionam no título estático',
    ],
  },

  // Nodes CRM - Alterar Fase
  crm_change_phase: {
    title: 'Alterar Fase CRM',
    description: 'Move um negócio existente para outra fase do pipeline, permitindo atualizar o status conforme o fluxo avança.',
    rules: [
      'Selecione como encontrar o deal: mais recente ou de variável',
      'Para "mais recente", defina o filtro de status (aberto, ganho, perdido, todos)',
      'A fase destino deve pertencer ao mesmo pipeline do deal',
      'Se nenhum deal for encontrado, o node é pulado silenciosamente',
    ],
    examples: [
      'Deal mais recente (Em Aberto) → Fase: Em Negociação',
      'Deal de variável {{deal_id}} → Fase: Proposta Enviada',
      'Deal mais recente (Todos) → Fase: Fechado Ganho',
    ],
    tips: [
      '💡 Use "De variável" quando precisar de precisão (recomendado)',
      '💡 "Mais recente" é útil para fluxos simples com um deal por contato',
      '💡 O filtro de status evita mover deals já finalizados',
      '💡 Fases com ícone verde são de "Ganho", vermelho de "Perdido"',
      '💡 Combine com "Criar Negócio" para fluxos de vendas completos',
    ],
  },

  // Nodes CRM - Criar Atividade no Negócio (timeline)
  crm_create_activity: {
    title: 'Criar Atividade no Negócio',
    description: 'Registra automaticamente uma atividade (nota, ligação, e-mail, reunião, lembrete, WhatsApp ou visita) na timeline de um negócio do CRM. Persistência feita pela mesma RPC `create_deal_activity` usada quando o usuário cria atividades manualmente no modal do deal.',
    rules: [
      'O fluxo precisa carregar um deal — via gatilho CRM (entity_type=deal) OU via variável {{deal_id}}',
      'Tipo é obrigatório (note, call, email, meeting, task, whatsapp, visit)',
      'Título é obrigatório (máx. 200 caracteres) e aceita {{variaveis}}',
      'Descrição é opcional (máx. 2000 caracteres) e aceita {{variaveis}}',
      'Responsável é opcional: usuário fixo, vendedor do negócio (runtime) ou variável com user_id',
      'Esta versão NÃO trata agendamento — toda atividade entra como histórico (scheduled_at = NULL)',
      'A variável de saída permite reutilizar o ID da atividade em nodes seguintes',
    ],
    examples: [
      'Tipo: Ligação | Título: "Follow-up — {{contact_name}}" | Negócio: do gatilho',
      'Tipo: Nota | Título: "Lead qualificado via WhatsApp" | Responsável: Vendedor do negócio',
      'Tipo: Reunião | Título: "Apresentação de proposta" | Salvar ID em: ultima_atividade_id',
    ],
    tips: [
      '💡 Use após "Alterar Fase" para registrar o motivo da mudança na timeline',
      '💡 Combine com "Criar Negócio" + variável de saída do deal_id para registrar atividades em deals novos',
      '💡 Para atividades agendadas (com data/hora), use o módulo de Cadência ou o formulário manual',
      '💡 O Responsável "Vendedor do negócio" é resolvido em runtime — funciona mesmo quando o vendedor muda',
    ],
  },

  // Node Tarefas - Alterar Fase da Tarefa (RELEASE 1.0.0 - 2026-05-31)
  task_change_phase: {
    title: 'Alterar Fase da Tarefa',
    description: 'Move uma tarefa existente para outra fase da mesma categoria. Usa a RPC `move_task_phase` (mesma do módulo Tarefas) com bypass híbrido auth.uid()/service_role — todas as validações de negócio continuam ativas, apenas a permissão por fase é dispensada no contexto de automação.',
    rules: [
      'A tarefa precisa existir e pertencer ao mesmo tenant do fluxo',
      'A fase de destino deve pertencer à MESMA categoria da tarefa (validação server-side)',
      'A origem da tarefa pode vir do gatilho de Tarefa (entity_type="task") ou de variável do fluxo',
      'Permissões por fase (can_change_phase) são desligadas apenas para chamadas do Flow Engine',
      'Erros de banco são propagados e fazem o step falhar com a mensagem original',
    ],
    examples: [
      'Gatilho "Tarefa entrou em fase X" → Alterar Fase → "Em execução"',
      'Após "Criar Tarefa" salvar ID em {{task_id}} → Alterar Fase para fase intermediária',
      'Webhook externo carrega {{task_id}} → Alterar Fase para "Concluída"',
    ],
    tips: [
      '💡 Combine com "Atribuir Responsável" antes da mudança de fase',
      '💡 Se a fase escolhida for de outra categoria em runtime, o node falha com mensagem clara',
      '💡 O dono do fluxo é o responsável pela ação — não há checagem de permissão por fase',
    ],
  },

  // Node Tarefas - Atribuir Responsável (RELEASE 1.0.0 - 2026-05-31)
  task_assign: {
    title: 'Atribuir Responsável da Tarefa',
    description: 'Define ou remove o responsável de uma tarefa existente via RPC `update_task` (mesma do módulo Tarefas) com bypass híbrido auth.uid()/service_role. Suporta usuário fixo, vendedor do negócio vinculado, variável do fluxo ou remoção total do responsável.',
    rules: [
      'A tarefa precisa existir no tenant do fluxo',
      'No modo "Vendedor do negócio": usa o seller_id da tarefa (ou do negócio do contexto) e resolve para user_id',
      'No modo "Variável": a variável precisa conter um user_id válido (membro do tenant)',
      'No modo "Remover": limpa assigned_to (assigned_to = NULL)',
      'Todas as demais validações da RPC update_task continuam ativas (tenant, categoria, etc.)',
    ],
    examples: [
      'Gatilho "Tarefa criada" → Vendedor do negócio (resolve em runtime)',
      'Após "Criar Tarefa" + {{task_id}} → Usuário fixo (gerente da equipe)',
      'Reatribuir todas as tarefas de uma fase para variável {{novo_responsavel}}',
      'Após conclusão → Remover responsável (libera para outra equipe pegar)',
    ],
    tips: [
      '💡 Use "Vendedor do negócio" para roteamento automático conforme dono do negócio',
      '💡 Combine com "Alterar Fase da Tarefa" para coreografias de SLA',
      '💡 Se o user_id da variável não existir, a RPC falha e o step é marcado como erro',
    ],
  },

  /** Node de rastreamento Google Tag Manager */
  tracking_gtm: {
    title: 'Google Tag Manager (GTM)',
    description: 'Dispara eventos customizados no dataLayer do Google Tag Manager quando o visitante passa por este ponto do fluxo. Isso permite rastrear conversões e ações diretamente no GTM.',
    rules: [
      'O campo "Nome do Evento" é obrigatório e será o nome do evento no dataLayer',
      'Variáveis usam a sintaxe {{nome_variavel}} e são substituídas por valores coletados no fluxo',
      'Parâmetros UTM (utm_source, utm_medium, utm_campaign, etc.) são capturados automaticamente da URL',
      'O GTM ID deve ser configurado na aba "Rastreamento" das opções de compartilhamento do fluxo',
      'O evento é disparado via dataLayer.push({ event: "nome", ...variáveis })',
    ],
    examples: [
      'Evento: generate_lead → lead_source: {{utm_source}}, lead_name: {{nome}}',
      'Evento: step_qualification → step: 2, canal: {{utm_medium}}',
      'Evento: form_submit → form_id: contato, telefone: {{telefone}}',
    ],
    tips: [
      'Use variáveis coletadas anteriormente ({{nome}}, {{email}}, {{telefone}}) nos valores',
      'Parâmetros UTM ficam disponíveis automaticamente: {{utm_source}}, {{utm_medium}}, {{utm_campaign}}, {{utm_term}}, {{utm_content}}',
      'Adicione nodes GTM em pontos estratégicos do fluxo para criar funis de conversão no GTM',
      'No GTM, crie triggers do tipo "Evento personalizado" usando o nome configurado aqui',
      'Click IDs como {{gclid}} e {{fbclid}} também são capturados automaticamente da URL',
    ],
  },

  /** Node de rastreamento Meta Pixel (Facebook) */
  tracking_meta_pixel: {
    title: 'Meta Pixel (Facebook)',
    description: 'Dispara eventos de conversão do Facebook/Instagram diretamente do fluxo. Suporta eventos padrão (Lead, Purchase, AddToCart) e eventos customizados, com parâmetros dinâmicos.',
    rules: [
      'Selecione um tipo de evento padrão ou escolha "Evento Customizado" para criar o seu',
      'Eventos padrão (Lead, Purchase, etc.) são reconhecidos automaticamente pelo Meta Ads',
      'Parâmetros como currency e value permitem otimizar campanhas por valor de conversão',
      'O Pixel ID deve ser configurado na aba "Rastreamento" das opções de compartilhamento do fluxo',
      'O evento é disparado via fbq("track", "NomeEvento", { params }) ou fbq("trackCustom", ...)',
    ],
    examples: [
      'Evento: Lead → content_name: "Formulário Principal", lead_source: {{utm_source}}',
      'Evento: Purchase → currency: BRL, value: {{valor_plano}}',
      'Evento Customizado: "meu_evento" → etapa: qualificação, canal: {{utm_medium}}',
    ],
    tips: [
      'Use eventos padrão sempre que possível — eles ativam otimizações automáticas no Meta Ads',
      'O evento "Lead" é ideal para rastrear geração de leads nos formulários do fluxo',
      'Para e-commerce, use "Purchase" com currency e value para calcular ROAS',
      'Variáveis dinâmicas {{variavel}} funcionam nos campos de parâmetros',
      'Use "Evento Customizado" apenas quando nenhum evento padrão se aplicar',
    ],
  },

  /** Node de rastreamento Google Analytics 4 */
  tracking_ga4: {
    title: 'Google Analytics 4 (GA4)',
    description: 'Dispara eventos personalizados no Google Analytics 4 via gtag("event", ...) quando o visitante passa por este ponto do fluxo. Ideal para análise de comportamento e funis.',
    rules: [
      'O campo "Nome do Evento" é obrigatório e será enviado ao GA4',
      'Parâmetros customizados aparecem como dimensões nos relatórios do GA4',
      'Variáveis usam a sintaxe {{nome_variavel}} e são resolvidas automaticamente',
      'O Measurement ID (G-XXXXX) deve ser configurado na aba "Rastreamento" das opções de compartilhamento',
      'O evento é disparado via gtag("event", "nome", { param1: "valor1", ... })',
    ],
    examples: [
      'Evento: flow_start → source: {{utm_source}}, campaign: {{utm_campaign}}',
      'Evento: generate_lead → value: {{valor}}, currency: BRL',
      'Evento: step_complete → step_name: qualificação, lead_name: {{nome}}',
    ],
    tips: [
      'Use nomes de eventos recomendados pelo GA4 (generate_lead, sign_up, purchase) para ativar relatórios automáticos',
      'Parâmetros UTM são populados automaticamente: {{utm_source}}, {{utm_medium}}, {{utm_campaign}}',
      'Cada parâmetro aparece como dimensão no GA4 — crie relatórios personalizados com eles',
      'Configure o Measurement ID (G-XXXXX) na aba de Rastreamento do compartilhamento do fluxo',
      'Combine múltiplos nodes GA4 no fluxo para criar funis de conversão detalhados',
    ],
  },

  /** Node de incorporação de conteúdo externo (embed/iframe) */
  embed: {
    title: 'Incorporar (Embed)',
    description: 'Permite incorporar conteúdo externo (sites, vídeos, PDFs, formulários) diretamente no chat via iframe. Ideal para exibir vídeos do YouTube, formulários, mapas, etc.',
    rules: [
      'A URL deve ser pública e permitir incorporação (X-Frame-Options / CSP)',
      'Suporta variáveis dinâmicas usando a sintaxe {{variavel}}',
      'Altura configurável entre 100px e 1200px (padrão: 400px)',
      'O iframe é sandboxed por segurança (scripts e formulários permitidos)',
      'Alguns sites bloqueiam incorporação (ex: Google, Facebook) — teste antes',
    ],
    examples: [
      'Vídeo YouTube: https://www.youtube.com/embed/VIDEO_ID',
      'Vídeo Vimeo: https://player.vimeo.com/video/VIDEO_ID',
      'Google Maps: https://www.google.com/maps/embed?pb=...',
      'Formulário Typeform: https://FORM_ID.typeform.com/to/FORM_SLUG',
      'PDF hospedado: https://exemplo.com/arquivo.pdf',
    ],
    tips: [
      '💡 Para YouTube, use a URL de embed (youtube.com/embed/...) e não a URL padrão',
      '💡 Use variáveis para personalizar a URL: https://meusite.com/perfil/{{user_id}}',
      '💡 Ajuste a altura para o tipo de conteúdo: vídeos ficam bem com 315px, formulários com 500px+',
      '💡 Teste a URL diretamente no navegador para garantir que permite incorporação',
      '💡 O conteúdo é carregado em sandbox seguro — scripts e formulários funcionam normalmente',
    ],
  },
};

interface NodeHelpDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  nodeType: string;
}

/**
 * Componente de modal de ajuda
 */
export function NodeHelpDialog({ open, onOpenChange, nodeType }: NodeHelpDialogProps) {
  const content = nodeHelpData[nodeType];

  if (!content) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-primary" />
            {content.title}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-2">
          {/* Descrição */}
          <div>
            <p className="text-muted-foreground">{content.description}</p>
          </div>

          <Separator />

          {/* Regras */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <h3 className="font-semibold">Regras e Requisitos</h3>
            </div>
            <ul className="space-y-2 ml-6">
              {content.rules.map((rule, index) => (
                <li key={index} className="text-sm text-foreground flex items-start gap-2">
                  <span className="text-muted-foreground mt-1">•</span>
                  <span>{rule}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Operadores (apenas para condição) */}
          {content.operators && (
            <>
              <Separator />
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Code className="h-4 w-4 text-blue-600" />
                  <h3 className="font-semibold">Operadores Disponíveis</h3>
                </div>
                <div className="grid grid-cols-2 gap-2 ml-6">
                  {Object.entries(content.operators).map(([op, desc]) => (
                    <div key={op} className="flex items-center gap-2">
                      <Badge variant="secondary" className="font-mono text-xs">
                        {op}
                      </Badge>
                      <span className="text-xs text-muted-foreground">{desc}</span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Exemplos */}
          {content.examples && content.examples.length > 0 && (
            <>
              <Separator />
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Code className="h-4 w-4 text-purple-600" />
                  <h3 className="font-semibold">Exemplos de Uso</h3>
                </div>
                <div className="space-y-2 ml-6">
                  {content.examples.map((example, index) => (
                    <div key={index} className="bg-muted/50 rounded p-3">
                      <code className="text-sm text-foreground">{example}</code>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Dicas */}
          {content.tips && content.tips.length > 0 && (
            <>
              <Separator />
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Lightbulb className="h-4 w-4 text-amber-600" />
                  <h3 className="font-semibold">Dicas e Boas Práticas</h3>
                </div>
                <ul className="space-y-2 ml-6">
                  {content.tips.map((tip, index) => (
                    <li key={index} className="text-sm text-muted-foreground flex items-start gap-2">
                      <span className="mt-1">💡</span>
                      <span>{tip}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}